import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Trash2 } from 'lucide-react'
import { useDeleteUserSuggestion } from '@/hooks/useUserSuggestions'
import { useToast } from '@/hooks/use-toast'
import type { Suggestion } from '@brent/shared/types'

interface DeleteUserSuggestionDialogProps {
  username: string
  suggestion: Suggestion
  memberName: string
}

export const DeleteUserSuggestionDialog = ({
  username,
  suggestion,
  memberName,
}: DeleteUserSuggestionDialogProps) => {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const deleteMutation = useDeleteUserSuggestion(username)

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(suggestion.suggestion_id)
      toast({
        title: 'Suggestion deleted',
        description: `Deleted "${suggestion.composer} - ${suggestion.title}" for ${memberName}`,
      })
      setOpen(false)
    } catch (error) {
      toast({
        title: 'Failed to delete suggestion',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Suggestion?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete "{suggestion.composer} - {suggestion.title}" for{' '}
            {memberName}? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
